import React, { useState, useRef } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, GridLegacy as Grid } from "@mui/material";
import { CloudUpload as UploadIcon, Image as ImageIcon } from "@mui/icons-material";
import { ComplaintImage } from "../BookingComplaint";
import { ImageUploadDropZone } from "./ImageUploadDropZone";
import { ImageGrid } from "./ImageGrid";
import { useTranslation } from "react-i18next";
import { theme } from "../../../../theme";

interface ImageSelectionModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (selectedImages: ComplaintImage[]) => Promise<void>;
    complaintImages: ComplaintImage[];
    availableInspectionImages: ComplaintImage[];
    selectedImages: ComplaintImage[];
    uploadingImages: Set<string>;
    onFilesSelected: (files: FileList) => void;
    onImageSelect: (image: ComplaintImage) => void;
    isUploading: boolean;
}

export const ImageSelectionModal: React.FC<ImageSelectionModalProps> = ({
    open,
    onClose,
    onSave,
    complaintImages,
    availableInspectionImages,
    selectedImages,
    uploadingImages,
    onFilesSelected,
    onImageSelect,
    isUploading
}) => {
    const [dragActive, setDragActive] = useState(false);
    const { t } = useTranslation();
    const uploadedImagesRef = useRef<HTMLDivElement>(null);

    const processFiles = (files: FileList | File[]) => {
        if (!files || files.length === 0) return;

        const imageFiles = Array.from(files).filter(file => file.type.startsWith("image/"));

        if (imageFiles.length === 0) {
            alert("Please select only image files.");
            return;
        }

        if (imageFiles.length < files.length) {
            alert("Some files were skipped because they are not images. Only image files are allowed.");
        }

        const maxSize = 5 * 1024 * 1024; // 5MB
        const oversizedFiles = imageFiles.filter(file => file.size > maxSize);

        if (oversizedFiles.length > 0) {
            alert(`Some files exceed the 5MB limit and cannot be uploaded.`);
            return;
        }

        onFilesSelected(imageFiles as unknown as FileList);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setDragActive(false);

        const files = event.dataTransfer.files;
        if (files && files.length > 0) {
            processFiles(files);
            scrollToBottom();
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setDragActive(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        const rect = event.currentTarget.getBoundingClientRect();
        const x = event.clientX;
        const y = event.clientY;

        // Set dragActive to false if we"re leaving the drop zone
        if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
            setDragActive(false);
        }
    };

    const handleFilesSelected = (files: FileList) => {
        processFiles(files);
        scrollToBottom();
    };

    const uploadedImages = complaintImages.filter(img => !img.isInspectionImage);
    const isSelectionEmpty = selectedImages.length === 0;

    const scrollToBottom = () => {
        setTimeout(() => {
            if (uploadedImagesRef.current) {
                uploadedImagesRef.current.scrollTo({
                    top: uploadedImagesRef.current.scrollHeight,
                    behavior: 'smooth'
                });
            }
        }, 100);
    };

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="lg"
            onClose={onClose}
        >
            <DialogTitle>
                <div style={{ textAlign: "center", fontWeight: "700" }}>
                    {t("complaints.images.selectImages")}
                </div>
            </DialogTitle>
            <DialogContent ref={uploadedImagesRef}>
                <Box sx={{ p: 2 }}>
                    <ImageUploadDropZone
                        onFilesSelected={handleFilesSelected}
                        dragActive={dragActive}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    />

                    <Grid container spacing={3}>
                        {/* Uploaded Images */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                                {t("complaints.imageType.uploaded")}
                            </Typography>
                            <ImageGrid
                                images={uploadedImages}
                                selectedImages={selectedImages}
                                uploadingImages={uploadingImages}
                                onImageSelect={onImageSelect}
                                showDelete={false}
                                emptyStateMessage={t("complaints.images.noUploadedImages")}
                                emptyStateIcon={<UploadIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />}
                                gridSize="medium"
                            />
                        </Grid>

                        {/* Inspection Images */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                                {t("complaints.imageType.inspection")}
                            </Typography>
                            <ImageGrid
                                images={availableInspectionImages}
                                selectedImages={selectedImages}
                                onImageSelect={onImageSelect}
                                showDelete={false}
                                emptyStateMessage={t("complaints.images.noInspectionImages")}
                                emptyStateIcon={<ImageIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />}
                                gridSize="medium"
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    sx={{ color: "#666" }}
                    disabled={isUploading}
                >
                    {t("general.cancel")}
                </Button>
                <Button
                    onClick={() => onSave(selectedImages)}
                    sx={{
                        bgcolor: theme.palette.brand.main,
                        color: "white",
                        "&:hover": {
                            bgcolor: "#b91c1c"
                        },
                        "&:disabled": {
                            bgcolor: "#9ca3af",
                            color: "white"
                        }
                    }}
                    disabled={isSelectionEmpty || isUploading}
                >
                    {isUploading ? t("general.loading") : t("general.save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}; 